import React, { useState, useEffect, useRef } from "react";
import ResultsOnPage from "./ResultsOnPage";
import PageNavi from "./PageNavi";
import DropdownFilter from "./Filters/DropdownFilter";
import SiteList from "./SiteList";
import TextFilter from "./Filters/TextFilter";
import DropdownTree from "../CustomComponents/DropdownTree";
import Api from "../../services/Api";
import axios from "axios";
import { IconButton } from "@fluentui/react";
import ExportButton from "./ExportButton";
import { Stack } from "@fluentui/react";
import { Parser } from "json2csv";

const CancelToken = axios.CancelToken;

const AdministrationPanel = ({ modalId }) => {
  const apiAddress = "/SitesPanel";

  const fields = [
    {
      label: "Site Name",
      value: "title",
    },
    {
      label: "Site Url",
      value: "url",
    },
    {
      label: "Owner",
      value: "owner",
    },
    {
      label: "Expiration Date",
      value: "expirationDate",
    },
    {
      label: "Site Status",
      value: "status",
    },
    {
      label: "Provisioning Status",
      value: "provisioningStatus",
    },
    {
      label: "External Collaboration",
      value: "externalCollaboration",
    },
    {
      label: "Confidentiality",
      value: "confidentiality",
    },
    {
      label: "Organization",
      value: "organization",
    },
    {
      label: "Site Audience",
      value: "siteAudience",
    },
    {
      label: "Template",
      value: "template",
    },
    {
      label: "Attention",
      value: "attentionType",
    },
    {
      label: "Create Date",
      value: "createdDate",
    },
  ];

  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [sites, setSites] = useState([]);
  const [totalResults, setTotalResults] = useState(0);
  const [onPage, setOnPage] = useState(20);
  const [pageUrlFilter, setPageUrlFilter] = useState(1);
  const [filters, setFilters] = useState({});
  const [orderBy, setOrderBy] = useState({
    fieldName: "",
    isDescending: false,
  });
  const cancelSource = useRef(null);

  useEffect(() => {
    return updateResults();
  }, [onPage, pageUrlFilter, filters, orderBy]);

  const loadSites = (posts) => {
    setSites(posts);
    setIsLoading(false);
  };

  const loadTotalCount = (total) => {
    setTotalResults(total);
  };

  const updateResults = () => {
    setIsLoading(true);

    let url = `${apiAddress}?page=${pageUrlFilter}&onPage=${onPage}`;

    const filterUrl = Object.keys(filters).reduce(
      (queryString, filterField) => {
        const filterText = filters[filterField];
        if (filterText) {
          queryString += `&${filterField}=${encodeURIComponent(filterText)}`;
        }
        return queryString;
      },
      ""
    );

    let orderByUrl = "";
    if (orderBy.fieldName) {
      orderByUrl = `&orderBy=${orderBy.fieldName}&isDesc=${orderBy.isDescending}`;
    }

    if (!modalId) {
      url = url + filterUrl + orderByUrl;
    } else {
      url = url + `&id=${modalId}` + orderByUrl;
    }

    if (cancelSource.current) {
      cancelSource.current.cancel();
    }

    cancelSource.current = CancelToken.source();

    Api.get(url, {
      cancelToken: cancelSource.current.token,
    })
      .then((res) => res.data)
      .then((posts) => {
        if (modalId) {
          loadSites([posts]);
          loadTotalCount(posts ? 1 : 0);
        } else {
          loadSites(posts.results);
          loadTotalCount(posts.totalCount);
        }
      })
      .catch((err) => {});

    return () => {
      cancelSource.current.cancel();
    };
  };

  const onFilterChange = (fieldName, value) => {
    setFilters({
      ...filters,
      [fieldName]: value,
    });
    setPageUrlFilter(1);
  };

  const handleNoSelection = () => {
    loadSites([]);
    loadTotalCount(0);
  };

  const sortItems = (fieldName, isDescending) => {
    setOrderBy({
      fieldName,
      isDescending,
    });
  };

  function getAndExportSites() {
    setIsExporting(true);

    let url = `${apiAddress}?forExport=${1}`;

    const filterUrl = Object.keys(filters).reduce(
      (queryString, filterField) => {
        const filterText = filters[filterField];
        if (filterText) {
          queryString += `&${filterField}=${encodeURIComponent(filterText)}`;
        }
        return queryString;
      },
      ""
    );

    let orderByUrl = "";
    if (orderBy.fieldName) {
      orderByUrl = `&orderBy=${orderBy.fieldName}&isDesc=${orderBy.isDescending}`;
    }

    url = url + filterUrl + orderByUrl;

    if (cancelSource.current) {
      cancelSource.current.cancel();
    }

    cancelSource.current = CancelToken.source();

    Api.get(url, {
      cancelToken: cancelSource.current.token,
    })
      .then((res) => res.data)
      .then((posts) => {
        createAndDownloadCsv(posts, "sites", fields);
        setIsExporting(false);
      })
      .catch((err) => {});

    return () => {
      cancelSource.current.cancel();
    };
  }

  function createAndDownloadCsv(items, fileName, fields) {
    //withBOM parameter is required to support Unicode characters
    let opts = { fields: fields, delimiter: ";", withBOM: true };

    items.forEach((item) => {
      if (Array.isArray(item.attentionType) && item.attentionType.length > 0) {
        item.attentionType = item.attentionType.join(", ");
      }
      if (Array.isArray(item.owner) && item.owner.length > 0) {
        item.owner = item.owner.join(", ");
      }
    });

    let parser = new Parser(opts);
    let csv = parser.parse(items);
    let formattedData = csv.replace(/"/g, "").replace(/T00:00:00/g, "");

    let link = document.createElement("a");
    link.setAttribute(
      "href",
      URL.createObjectURL(
        new Blob([formattedData], { type: "application/octet-stream" })
      )
    );
    link.setAttribute("download", `${fileName}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  const exportSites = () => {
    getAndExportSites();
  };

  return (
    <div>
      <div className="filters">
        <div className="cell">
          <TextFilter
            fieldName="title"
            fieldLabel="Site Name"
            onChange={onFilterChange}
          />
        </div>
        <div className="cell">
          <TextFilter
            fieldName="url"
            fieldLabel="Site URL"
            onChange={onFilterChange}
          />
        </div>
        <div className="cell">
          <TextFilter
            fieldName="owner"
            fieldLabel="Owner"
            onChange={onFilterChange}
          />
        </div>
        <div className="cell">
          <DropdownFilter
            fieldName="status"
            urlFetch="/SiteStatus?q=all"
            title="Site Status"
            onChange={onFilterChange}
            noselection={handleNoSelection}
            includeSelectAll={true}
          />
        </div>
        <div className="cell">
          <DropdownFilter
            fieldName="confid"
            urlFetch="/ConfidentalLevel?q=all"
            title="Confidential Level"
            onChange={onFilterChange}
            noselection={handleNoSelection}
          />
        </div>
        <div className="cell">
          <DropdownFilter
            fieldName="location"
            urlFetch="/Location"
            title="Location"
            optionFieldName="name"
            onChange={onFilterChange}
            noselection={handleNoSelection}
          />
        </div>
        <div className="cell">
          <DropdownFilter
            fieldName="template"
            urlFetch="/Template?getAll=true"
            title="Template"
            optionFieldName="name"
            onChange={onFilterChange}
            noselection={handleNoSelection}
            group={true}
            includeSelectAll={true}
          />
        </div>
        <div className="cell">
          <DropdownFilter
            fieldName="provisioningStatus"
            urlFetch="/ProvisioningStatus?q=all"
            title="Provisioning Status"
            onChange={onFilterChange}
            noselection={handleNoSelection}
            includeSelectAll={true}
          />
        </div>
        <div className="cell" style={{ zIndex: 2002 }}>
          <DropdownTree
            address={"/Management?parent="}
            label="Organization"
            multiselect={false}
            onChange={onFilterChange}
          />
        </div>
        <div className="cell" style={{ zIndex: 2001 }}>
          <DropdownTree
            address={"/Geography?parent="}
            label="Site Audience"
            multiselect={true}
            onChange={onFilterChange}
          />
        </div>
        <div className="cell">
          <DropdownFilter
            fieldName="attentionType"
            urlFetch="/AttentionType"
            title="Attention"
            onChange={onFilterChange}
            defaultEmpty
          />
        </div>
        <div className="cell">
          <DropdownFilter
            fieldName="tasks"
            urlFetch="/TasksFilter"
            title="Tasks filter"
            onChange={onFilterChange}
            noselection={handleNoSelection}
            includeSelectAll={true}
            multiSelect={false}
          />
        </div>
      </div>
      <div style={{ position: "relative", margin: "20px 0" }}>
        <div>
          <IconButton
            style={{ color: "rgb(96, 94, 92)", position: "absolute" }}
            onClick={updateResults}
            iconProps={{ iconName: "refresh" }}
          />
          <PageNavi
            pageNumPart={setPageUrlFilter}
            totalResults={totalResults}
            resultsOnPage={onPage}
            pageNum={pageUrlFilter}
            showTotalResults={true}
          />

          <Stack horizontal disableShrink>
            <ResultsOnPage onPage={onPage} onPageResults={setOnPage} />
            <ExportButton
              onClick={exportSites}
              loading={isExporting}></ExportButton>
          </Stack>
        </div>
      </div>
      <SiteList
        isLoading={isLoading}
        results={sites}
        filters={filters}
        orderedBy={orderBy}
        sortItems={sortItems}
        modalId={modalId}
      />
    </div>
  );
};
export default AdministrationPanel;



// WEBPACK FOOTER //
// ./src/components/Administration/AdministrationPanel.js